import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useCreateNote } from '../../hooks/useAccountingNotes';

interface NoteAddModalProps {
  open: boolean;
  onClose: () => void;
  entityType: 'invoice' | 'bill';
  entityId: string;
  onSuccess?: () => void;
}

/**
 * NoteAddModal - Modal for creating a new note on an invoice or bill
 */
export const NoteAddModal: React.FC<NoteAddModalProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  onSuccess,
}) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createNoteMutation = useCreateNote();

  const handleSubmit = async () => {
    // Validate content
    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setError('Note content is required');
      return;
    }

    setError(null);

    try {
      await createNoteMutation.mutateAsync({
        entityType,
        entityId,
        content: trimmedContent,
      });

      // Success - reset form and close modal
      setContent('');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Error creating note:', err);
      setError(err.message || 'Failed to create note');
    }
  };

  const handleClose = () => {
    if (!createNoteMutation.isPending) {
      setContent('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Note to {entityType === 'invoice' ? 'Invoice' : 'Bill'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Note"
            fullWidth
            multiline
            rows={4}
            required
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={createNoteMutation.isPending}
            placeholder="Enter your note here..."
            autoFocus
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={createNoteMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createNoteMutation.isPending || !content.trim()}
        >
          {createNoteMutation.isPending ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Note'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteAddModal;
